import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

const D3TreeFlowchart = ({ data, onNodeClick, loading, type = 'operations' }) => {
  const svgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });

  useEffect(() => {
    const handleResize = () => {
      const container = svgRef.current?.parentElement;
      if (container) {
        setDimensions({
          width: container.clientWidth || 800,
          height: 500
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!data || loading || !svgRef.current) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current);
    const margin = { top: 60, right: 160, bottom: 60, left: 160 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    // Create dynamic hierarchy based on team type and actual data
    let hierarchyData;
    
    if (type === "customer-service") {
      // Customer Service hierarchy - use Operations-style structure but with Customer Service data
      hierarchyData = {
        name: "Customer Service",
        value: data.reduce((sum, item) => sum + (item.value || 0), 0),
        percentage: data.reduce((sum, item) => sum + (item.percentage || 0), 0) / data.length,
        children: [
          {
            id: "cs-touched-node",
            name: "Touched",
            value: data.find(item => item.label.toLowerCase().includes('touched') || item.label.toLowerCase().includes('contacted') || item.label.toLowerCase().includes('responded'))?.value || 0,
            percentage: data.find(item => item.label.toLowerCase().includes('touched') || item.label.toLowerCase().includes('contacted') || item.label.toLowerCase().includes('responded'))?.percentage || 0,
            process: data.find(item => item.label.toLowerCase().includes('touched') || item.label.toLowerCase().includes('contacted') || item.label.toLowerCase().includes('responded'))?.process,
            children: data.filter(item => 
              !item.label.toLowerCase().includes('touched') && 
              !item.label.toLowerCase().includes('contacted') && 
              !item.label.toLowerCase().includes('responded') &&
              !item.label.toLowerCase().includes('untouched') &&
              !item.label.toLowerCase().includes('not contacted')
            ).map((item, index) => ({
              id: `cs-process-${index}`,
              name: item.label,
              value: item.value,
              percentage: item.percentage,
              process: item.process
            }))
          },
          {
            id: "cs-untouched-node",
            name: "Untouched",
            value: data.find(item => item.label.toLowerCase().includes('untouched') || item.label.toLowerCase().includes('not contacted') || item.label.toLowerCase().includes('pending'))?.value || 0,
            percentage: data.find(item => item.label.toLowerCase().includes('untouched') || item.label.toLowerCase().includes('not contacted') || item.label.toLowerCase().includes('pending'))?.percentage || 0,
            process: data.find(item => item.label.toLowerCase().includes('untouched') || item.label.toLowerCase().includes('not contacted') || item.label.toLowerCase().includes('pending'))?.process,
            children: [] // Untouched has no children
          }
        ]
      };
    } else if (type === "credit-team") {
      // Credit Team hierarchy - use Operations-style structure but with Credit Team data
      hierarchyData = {
        name: "Credit Team",
        value: data.reduce((sum, item) => sum + (item.value || 0), 0),
        percentage: data.reduce((sum, item) => sum + (item.percentage || 0), 0) / data.length,
        children: [
          {
            id: "credit-touched-node",
            name: "Touched",
            value: data.find(item => item.label.toLowerCase().includes('touched') || item.label.toLowerCase().includes('contacted') || item.label.toLowerCase().includes('processed'))?.value || 0,
            percentage: data.find(item => item.label.toLowerCase().includes('touched') || item.label.toLowerCase().includes('contacted') || item.label.toLowerCase().includes('processed'))?.percentage || 0,
            process: data.find(item => item.label.toLowerCase().includes('touched') || item.label.toLowerCase().includes('contacted') || item.label.toLowerCase().includes('processed'))?.process,
            children: data.filter(item => 
              !item.label.toLowerCase().includes('touched') && 
              !item.label.toLowerCase().includes('contacted') && 
              !item.label.toLowerCase().includes('processed') &&
              !item.label.toLowerCase().includes('untouched') &&
              !item.label.toLowerCase().includes('not contacted')
            ).map((item, index) => ({
              id: `credit-process-${index}`,
              name: item.label,
              value: item.value,
              percentage: item.percentage,
              process: item.process
            }))
          },
          {
            id: "credit-untouched-node",
            name: "Untouched",
            value: data.find(item => item.label.toLowerCase().includes('untouched') || item.label.toLowerCase().includes('not contacted') || item.label.toLowerCase().includes('pending'))?.value || 0,
            percentage: data.find(item => item.label.toLowerCase().includes('untouched') || item.label.toLowerCase().includes('not contacted') || item.label.toLowerCase().includes('pending'))?.percentage || 0,
            process: data.find(item => item.label.toLowerCase().includes('untouched') || item.label.toLowerCase().includes('not contacted') || item.label.toLowerCase().includes('pending'))?.process,
            children: [] // Untouched has no children
          }
        ]
      };
    } else {
      // Operations team - keep original hardcoded structure
      hierarchyData = {
        name: "Operations",
        value: data.reduce((sum, item) => sum + (item.value || 0), 0),
        percentage: data.reduce((sum, item) => sum + (item.percentage || 0), 0) / data.length,
        children: [
          {
            id: "touched-node",
            name: "Touched",
            value: data.find(item => item.label.toLowerCase().includes('touched'))?.value || 0,
            percentage: data.find(item => item.label.toLowerCase().includes('touched'))?.percentage || 0,
            process: data.find(item => item.label.toLowerCase().includes('touched'))?.process,
            children: [
              {
                id: "quality-check",
                name: "Quality Check",
                value: data.find(item => item.label.toLowerCase().includes('quality'))?.value || 0,
                percentage: data.find(item => item.label.toLowerCase().includes('quality'))?.percentage || 0,
                process: data.find(item => item.label.toLowerCase().includes('quality'))?.process
              },
              {
                id: "nach-leads",
                name: "Nach Leads",
                value: data.find(item => item.label.toLowerCase().includes('nach'))?.value || 0,
                percentage: data.find(item => item.label.toLowerCase().includes('nach'))?.percentage || 0,
                process: data.find(item => item.label.toLowerCase().includes('nach'))?.process
              },
              {
                id: "esign",
                name: "Esign",
                value: data.find(item => item.label.toLowerCase().includes('esign') || item.label.toLowerCase().includes('e-sign'))?.value || 0,
                percentage: data.find(item => item.label.toLowerCase().includes('esign') || item.label.toLowerCase().includes('e-sign'))?.percentage || 0,
                process: data.find(item => item.label.toLowerCase().includes('esign') || item.label.toLowerCase().includes('e-sign'))?.process
              },
              {
                id: "rfd-leads",
                name: "Rfd Leads",
                value: data.find(item => item.label.toLowerCase().includes('rfd') || item.label.toLowerCase().includes('request') || item.label.toLowerCase().includes('lead'))?.value || 0,
                percentage: data.find(item => item.label.toLowerCase().includes('rfd') || item.label.toLowerCase().includes('request') || item.label.toLowerCase().includes('lead'))?.percentage || 0,
                process: data.find(item => item.label.toLowerCase().includes('rfd') || item.label.toLowerCase().includes('request') || item.label.toLowerCase().includes('lead'))?.process
              },
              {
                id: "pending",
                name: "Pending",
                value: data.find(item => item.label.toLowerCase().includes('pending') && !item.label.toLowerCase().includes('rejected'))?.value || 0,
                percentage: data.find(item => item.label.toLowerCase().includes('pending') && !item.label.toLowerCase().includes('rejected'))?.percentage || 0,
                process: data.find(item => item.label.toLowerCase().includes('pending') && !item.label.toLowerCase().includes('rejected'))?.process
              },
              {
                id: "rejected",
                name: "Rejected",
                value: data.find(item => item.label.toLowerCase().includes('rejected'))?.value || 0,
                percentage: data.find(item => item.label.toLowerCase().includes('rejected'))?.percentage || 0,
                process: data.find(item => item.label.toLowerCase().includes('rejected'))?.process
              }
            ]
          },
          {
            id: "untouched-node",
            name: "Untouched",
            value: data.find(item => item.label.toLowerCase().includes('untouched'))?.value || 0,
            percentage: data.find(item => item.label.toLowerCase().includes('untouched'))?.percentage || 0,
            children: [] // Untouched has no children
          }
        ]
      };
    }

    // Create tree layout with enhanced spacing
    const root = d3.hierarchy(hierarchyData);
    const treeLayout = d3.tree()
      .size([height, width])
      .separation((a, b) => {
        // Enhanced spacing for better visual separation
        if (a.depth === 1 && b.depth === 1) {
          // Main nodes (Touched and Untouched) - more horizontal separation
          return 3;
        }
        if (a.depth === 2 && b.depth === 2) {
          // Child nodes - moderate separation
          return 1.8;
        }
        if (a.parent === b.parent) {
          // Siblings - closer together
          return 1.5;
        }
        // Different branches - more separation
        return 2.5;
      });

    treeLayout(root);

    // Create main group
    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create links with enhanced styling
    const link = d3.linkHorizontal()
      .x(d => d.y)
      .y(d => d.x);

    g.selectAll(".link")
      .data(root.links())
      .enter().append("path")
      .attr("class", "link")
      .attr("d", link)
      .style("fill", "none")
      .style("stroke", d => {
        // Consistent default line color across all components
        return "#95a5a6"; // Default gray for all lines
      })
      .style("stroke-width", d => {
        if (d.source.depth === 0) return 4;
        return 3;
      })
      .style("opacity", 0.8)
      .style("stroke-linecap", "round")
      .style("stroke-dasharray", d => {
        // Dashed lines for pending and rejected
        if (d.target.data.name === "Pending") return "8,4";
        if (d.target.data.name === "Rejected") return "4,4";
        return "none";
      });

    // Create node groups
    const nodes = g.selectAll(".node")
      .data(root.descendants().filter(d => {
        if (type === 'customer-service') {
          // For customer service, include all nodes including depth 1
          return d.depth >= 1; // Include all customer service nodes
        }
        return d.depth > 0; // Filter out root for other types
      }))
      .enter().append("g")
      .attr("class", "node")
      .attr("transform", d => `translate(${d.y},${d.x})`)
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        if (onNodeClick && d.data.process) {
          onNodeClick(d.data.name, d.data.process);
        }
      });

    // Add node backgrounds with enhanced styling
    nodes.append("rect")
      .attr("x", d => {
        // Larger boxes for main nodes
        if (d.depth === 1) return -80;
        return -70;
      })
      .attr("y", d => {
        if (d.depth === 1) return -30;
        return -25;
      })
      .attr("width", d => {
        if (d.depth === 1) return 160;
        return 140;
      })
      .attr("height", d => {
        if (d.depth === 1) return 60;
        return 50;
      })
      .attr("rx", 12)
      .style("fill", d => {
        /* ============ CUSTOMER SERVICE FILL COLORS ============ */
        // Customer Service team uses unique color scheme with distinct node types
        // Each node type has specific gradient for clear visual hierarchy
        if (type === "customer-service") {
          // Main root node - Purple (professional/trustworthy)
          if (d.data.name === "Customer Service") return "url(#cs-main-gradient)";
          
          // Main branch nodes - Green (Touched) and Red (Untouched) for clear binary split
          if (d.data.name === "Touched") return "url(#touched-gradient)";
          if (d.data.name === "Untouched") return "url(#untouched-gradient)";

          
          // Child nodes - Each has unique color based on business logic
          // Priority order matters: more specific matches first
          if (d.data.name.includes("Mark as Completed")) return "url(#cs-mark-completed-gradient)"; // Yellow - second completion type
          if (d.data.name.includes("Assignments Completed")) return "url(#cs-mark-completed-gradient)";
          if (d.data.name.includes("Assignments Pending")) return "url(#cs-pending-gradient)"; // Slate - waiting/neutral status
          if (d.data.name.includes("Total Assigned")) return "url(#cs-assigned-gradient)"; // Rose - attention/action needed
          if (d.data.name.includes("In Progress")) return "url(#cs-progress-gradient)"; // Amber - energy/work happening
          if (d.data.name.includes("Completed")) return "url(#cs-completed-gradient)"; // Fallback violet for other completed types
          if (d.data.name.includes("Pending")) return "url(#cs-pending-gradient)"; // Slate - waiting/neutral status
          if (d.data.name.includes("Assignments Touched")) return "url(#cs-touched-gradient)"; // Blue - action/touch status
          if (d.data.name.includes("Communicated")) return "url(#cs-communicated-gradient)"; // Cyan - communication status
          if (d.data.name.includes("Approved")) return "url(#cs-approved-gradient)"; // Emerald Green - approval status
          if (d.data.name.includes("Rejected")) return "url(#cs-rejected-gradient)"; // Red - rejection status
          if (d.data.name.includes("Expire")) return "url(#cs-expire-gradient)"; // Orange-Red - expiration status
          if (d.data.name.includes("LUP")) return "url(#cs-default-gradient)"; // Indigo - LUP specific
          return "url(#cs-default-gradient)"; // Indigo - Customer Service default for unmatched nodes
        }

        /* ============ CREDIT TEAM ============ */
        if (type === "credit-team") {
          if (d.data.name === "Credit Team") return "url(#credit-main-gradient)";
          if (d.data.name === "Touched") return "url(#touched-gradient)";
          if (d.data.name === "Untouched") return "url(#untouched-gradient)";
          // For child nodes, use different gradients based on their actual names
          if (d.data.name.includes("Total Assigned")) return "url(#credit-assigned-gradient)";
          if (d.data.name.includes("In Progress")) return "url(#credit-progress-gradient)";
          if (d.data.name.includes("Completed")) return "url(#credit-completed-gradient)";
          if (d.data.name.includes("Rejected")) return "url(#rejected-gradient)";
          if (d.data.name.includes("Assignments Pending")) return "url(#pending-gradient)";
          if (d.data.name.includes("Disbursed")) return "url(#cs-touched-gradient)";
          if (d.data.name.includes("Approved")) return "url(#cs-communicated-gradient)";
          if (d.data.name.includes("Expire")) return "url(#cs-expire-gradient)";
          if (d.data.name.includes("Mark as Incomplete")) return "url(#cs-default-gradient)";
          if (d.data.name.includes("Incomplete")) return "url(#cs-default-gradient)";
          return "url(#default-gradient)";
        }

        /* ============ OPERATIONS ============ */
        if (type === "operations") {
          if (d.data.name === "Touched") return "url(#touched-gradient)";
          if (d.data.name === "Untouched") return "url(#untouched-gradient)";
          if (d.data.name === "Quality Check") return "url(#quality-gradient)";
          if (d.data.name === "Nach Leads") return "url(#nach-gradient)";
          if (d.data.name === "Esign") return "url(#esign-gradient)";
          if (d.data.name === "Rfd Leads") return "url(#rfd-gradient)";
          if (d.data.name === "Pending") return "url(#pending-gradient)";
          if (d.data.name === "Rejected") return "url(#rejected-gradient)";
          return "url(#default-gradient)";
        }
      })
      .style("stroke", d => {
        /* ============ CUSTOMER SERVICE STROKE COLORS ============ */
        // Stroke colors match gradient start colors for visual consistency
        // Each node type has matching solid stroke color
        if (type === "customer-service") {
          // Main root node - Purple stroke
          if (d.data.name === "Customer Service") return "#6b46c1";
          
          // Main branch nodes - Green (Touched) and Red (Untouched) 
          if (d.data.name === "Touched") return "#27ae60";
          if (d.data.name === "Untouched") return "#c0392b";
          
          // Child nodes - Matching stroke colors for each gradient
          // Same priority order as fill logic for consistency
          if (d.data.name.includes("Mark as Completed")) return "#eab308"; // Yellow stroke
          if (d.data.name.includes("Assignments Completed")) return "#eab308"; // Yellow stroke (same as Mark as Completed)
          if (d.data.name.includes("Communicated")) return "#0891b2"; // Cyan stroke
          if (d.data.name.includes("Approved")) return "#10b981"; // Emerald Green stroke
          if (d.data.name.includes("Rejected")) return "#dc2626"; // Red stroke
          if (d.data.name.includes("Expire")) return "#ea580c"; // Orange-Red stroke
          if (d.data.name.includes("LUP")) return "#4f46e5"; // Indigo stroke
          if (d.data.name.includes("Total Assigned")) return "#e11d48"; // Rose stroke
          if (d.data.name.includes("In Progress")) return "#d97706"; // Amber stroke
          if (d.data.name.includes("Completed")) return "#7c3aed"; // Fallback violet stroke
          if (d.data.name.includes("Pending")) return "#475569"; // Slate stroke
          if (d.data.name.includes("Assignments Touched")) return "#2563eb"; // Blue stroke
          return "#4f46e5"; // Indigo stroke - Customer Service default for unmatched nodes
        }

        /* ============ CREDIT TEAM ============ */
        if (type === "credit-team") {
          if (d.data.name === "Credit Team") return "#1e3a8a";
          if (d.data.name === "Touched") return "#27ae60";
          if (d.data.name === "Untouched") return "#c0392b";
          // For child nodes, use different stroke colors based on their actual names
          if (d.data.name.includes("Total Assigned")) return "#7c3aed";
          if (d.data.name.includes("In Progress")) return "#ea580c";
          if (d.data.name.includes("Completed")) return "#059669";
          if (d.data.name.includes("Rejected")) return "#c0392b";
          if (d.data.name.includes("Assignments Pending")) return "#f39c12";
          if (d.data.name.includes("Disbursed")) return "#2563eb";
          if (d.data.name.includes("Approved")) return "#0891b2";
          if (d.data.name.includes("Expire")) return "#ea580c";
          if (d.data.name.includes("Mark as Incomplete")) return "#4f46e5";
          if (d.data.name.includes("Incomplete")) return "#4f46e5";
          return "#7f8c8d";
        }

        /* ============ OPERATIONS ============ */
        if (type === "operations") {
          if (d.data.name === "Touched") return "#27ae60";
          if (d.data.name === "Untouched") return "#c0392b";
          if (d.data.name === "Quality Check") return "#8e44ad";
          if (d.data.name === "Nach Leads") return "#2980b9";
          if (d.data.name === "Esign") return "#27ae60";
          if (d.data.name === "Rfd Leads") return "#e67e22";
          if (d.data.name === "Pending") return "#f39c12";
          if (d.data.name === "Rejected") return "#c0392b";
          return "#3e8baa";
        }
      })
      .style("stroke-width", 3)
      .style("filter", "drop-shadow(0 4px 8px rgba(0,0,0,0.15))")
      .style("transition", "all 0.3s ease");

    // Add gradients
    const defs = svg.append("defs");
    
    // ================= CUSTOMER SERVICE GRADIENTS ================= */
    // Customer Service team uses unique color scheme different from Operations and Credit teams
    // Each node type has distinct gradient and matching stroke color for clear visual hierarchy

    // CS Main - Purple theme (root node, professional/trustworthy)
    defs.append("linearGradient")
      .attr("id", "cs-main-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#6b46c1" },
        { offset: "100%", color: "#9333ea" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Total Assigned - Rose theme (attention/action needed)
    defs.append("linearGradient")
      .attr("id", "cs-assigned-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#e11d48" },
        { offset: "100%", color: "#f43f5e" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS In Progress - Amber theme (energy/work happening)
    defs.append("linearGradient")
      .attr("id", "cs-progress-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#d97706" },
        { offset: "100%", color: "#f59e0b" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Assignments Completed - Violet theme (first completion type)
    defs.append("linearGradient")
      .attr("id", "cs-completed-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#7c3aed" },
        { offset: "100%", color: "#a78bfa" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Mark as Completed - Yellow theme (matches stroke color)
    defs.append("linearGradient")
      .attr("id", "cs-mark-completed-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#eab308" },
        { offset: "100%", color: "#fbbf24" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Communicated - Cyan theme (communication status, distinct from others)
    defs.append("linearGradient")
      .attr("id", "cs-communicated-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#0891b2" },
        { offset: "100%", color: "#06b6d4" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Assignments Touched - Blue theme (action/touch status)
    defs.append("linearGradient")
      .attr("id", "cs-touched-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#2563eb" },
        { offset: "100%", color: "#3b82f6" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Approved - Emerald Green theme (approval status, distinct from others)
    defs.append("linearGradient")
      .attr("id", "cs-approved-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#10b981" },
        { offset: "100%", color: "#34d399" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Rejected - Red theme (rejection status, clear negative indicator)
    defs.append("linearGradient")
      .attr("id", "cs-rejected-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#dc2626" },
        { offset: "100%", color: "#ef4444" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Expire - Orange-Red theme (expiration status, warning indicator)
    defs.append("linearGradient")
      .attr("id", "cs-expire-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#ea580c" },
        { offset: "100%", color: "#f97316" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Default/Unmatched - Indigo theme (for nodes that don't match specific types)
    defs.append("linearGradient")
      .attr("id", "cs-default-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#4f46e5" },
        { offset: "100%", color: "#6366f1" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // CS Pending - Slate theme (waiting/neutral status)
    defs.append("linearGradient")
      .attr("id", "cs-pending-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#475569" },
        { offset: "100%", color: "#64748b" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);
    
    // ================= CREDIT TEAM GRADIENTS ================= */

    // Credit Main
    defs.append("linearGradient")
      .attr("id", "credit-main-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#1e3a8a" },
        { offset: "100%", color: "#3b82f6" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Credit Total Assigned
    defs.append("linearGradient")
      .attr("id", "credit-assigned-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#7c3aed" },
        { offset: "100%", color: "#a78bfa" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Credit In Progress
    defs.append("linearGradient")
      .attr("id", "credit-progress-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#ea580c" },
        { offset: "100%", color: "#fb923c" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Credit Completed
    defs.append("linearGradient")
      .attr("id", "credit-completed-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#059669" },
        { offset: "100%", color: "#34d399" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Touched gradient
    defs.append("linearGradient")
      .attr("id", "touched-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#27ae60" },
        { offset: "100%", color: "#2ecc71" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Untouched gradient
    defs.append("linearGradient")
      .attr("id", "untouched-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#c0392b" },
        { offset: "100%", color: "#e74c3c" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Quality Check gradient
    defs.append("linearGradient")
      .attr("id", "quality-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#8e44ad" },
        { offset: "100%", color: "#9b59b6" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Nach Leads gradient
    defs.append("linearGradient")
      .attr("id", "nach-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#2980b9" },
        { offset: "100%", color: "#3498db" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Esign gradient
    defs.append("linearGradient")
      .attr("id", "esign-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#27ae60" },
        { offset: "100%", color: "#2ecc71" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // RFD Leads gradient
    defs.append("linearGradient")
      .attr("id", "rfd-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#e67e22" },
        { offset: "100%", color: "#f39c12" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Pending gradient
    defs.append("linearGradient")
      .attr("id", "pending-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#f39c12" },
        { offset: "100%", color: "#f1c40f" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Rejected gradient
    defs.append("linearGradient")
      .attr("id", "rejected-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#c0392b" },
        { offset: "100%", color: "#e74c3c" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Default gradient
    defs.append("linearGradient")
      .attr("id", "default-gradient")
      .attr("x1", "0%").attr("y1", "0%")
      .attr("x2", "100%").attr("y2", "100%")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#ecf0f1" },
        { offset: "100%", color: "#bdc3c7" }
      ])
      .enter().append("stop")
      .attr("offset", d => d.offset)
      .attr("stop-color", d => d.color);

    // Add enhanced hover effects
    nodes
      .on("mouseover", function(event, d) {
        d3.select(this).select("rect")
          .style("filter", "drop-shadow(0 6px 12px rgba(0,0,0,0.25)) brightness(1.1)")
          .style("stroke-width", 4)
          .style("transform", "scale(1.05)");
      })
      .on("mouseout", function(event, d) {
        d3.select(this).select("rect")
          .style("filter", "drop-shadow(0 4px 8px rgba(0,0,0,0.15)) brightness(1)")
          .style("stroke-width", 3)
          .style("transform", "scale(1)");
      });

    // Icons removed as requested

    // Add node text with enhanced styling - centered and white
    nodes.append("text")
      .attr("dy", d => {
        if (d.depth === 1) return 8;  // Main nodes - text inside
        return -2;  // Child nodes - text inside
      })
      .attr("dx", 0)  // Center text
      .attr("text-anchor", "middle")  // Center alignment
      .style("font-size", d => {
        if (d.depth === 1) return "12px";
        return "10px";
      })
      .style("font-weight", "bold")
      .style("fill", "#ffffff")  // White color for all text
      .style("text-shadow", "0 1px 2px rgba(0,0,0,0.3)")  // Text shadow for readability
      .text(d => d.data.name);

    // Add value text with enhanced styling - centered and white
    nodes.append("text")
      .attr("dy", d => {
        if (d.depth === 1) return 25;  // Main nodes - value inside
        return 15;  // Child nodes - value inside
      })
      .attr("dx", 0)  // Center text
      .attr("text-anchor", "middle")  // Center alignment
      .style("font-size", d => {
        if (d.depth === 1) return "16px";
        return "14px";
      })
      .style("font-weight", "bold")
      .style("fill", "#ffffff")  // White color for all text
      .style("text-shadow", "0 1px 2px rgba(0,0,0,0.3)")  // Text shadow for readability
      .text(d => d.data.value);

    // Add percentage text with enhanced styling - inside boxes
    // Removed percentage text as requested

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.5, 3])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);

  }, [data, loading, dimensions, onNodeClick]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
        <div>Loading flowchart...</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '500px', overflow: 'auto' }}>
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        style={{ border: '1px solid #e5e7eb', borderRadius: '8px', background: '#ffffff' }}
      />
    </div>
  );
};

export default D3TreeFlowchart;
